import { classNames, Component, h, OmiProps, tag } from "omi";

import { installMadeForYou } from "@/wc/pages/project/features/directory/features/madeForYou/install.ts";
import { DirectoryStore } from "@/wc/pages/project/features/directory/store";
import { SearchResult } from "@/api/models/searchResult.ts";
import { NWBadge } from "@/wc/components/atoms/nw-badge";
import { NWError } from "@/wc/components/base/nw-error";
import { NWSuspense } from "@/wc/components";
import { tailwind } from "@/tailwind";

import { isCourse, Topic } from "../../store/models.ts";
import { DirectoryView } from "../../views";
import { TopicListSkeleton } from "./skeleton.tsx";

type Props = {
  topics: Topic[];
  isLoading: boolean;
  error: unknown | undefined;
  onSelect: (topic: Topic) => void;
};

@tag("nw-directory-topic-list")
export class TopicList extends Component<Props> {
  static css = [tailwind];

  installed() {
    if (DirectoryStore.isOnboardingModeActive()) {
      DirectoryStore.recommendedExpanded.value = false;
    }
  }

  private onSelect = (topic: Topic) => {
    this.props.onSelect(topic);
  };

  private navigateToMadeForYou = () => {
    DirectoryStore.show(DirectoryView.MADE_FOR_YOU);
  };

  render({ topics = [], isLoading, error }: Props) {
    const expanded = DirectoryStore.isRecommendedExpanded();
    const topicsToShow = expanded ? topics : topics.slice(0, 2);

    if (isLoading) {
      return <TopicListSkeleton />;
    }

    if (error != null) {
      return (
        <div class="animate-fadeInFast">
          <NWError />
        </div>
      );
    }

    return (
      <div
        className={classNames("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6", {
          "flex-wrap justify-center": expanded,
          "overflow-hidden": !expanded,
        })}
      >
        <NWSuspense
          loadFunc={installMadeForYou}
          onLoadComplete={({ MadeForYouTile }) => <MadeForYouTile onClick={this.navigateToMadeForYou} />}
          loadingComponent={() => <LoadingTile />}
        />
        {topicsToShow.map(topic => (
          <TopicButton topic={topic} onClick={() => this.onSelect(topic)} />
        ))}
      </div>
    );
  }
}

function LoadingTile() {
  return (
    <div
      className={classNames(
        "card shadow-md border border-brand-200 rounded-2xl p-4 h-52 flex-1 flex flex-col justify-end items-start relative overflow-hidden bg-leather",
      )}
    >
      <div className="card-header flex w-full flex-col items-start relative z-[1]">
        <div className="w-full h-6 bg-brand-900 animate-pulse rounded mb-1.5"></div>
        <div className="w-full h-6 bg-brand-900 animate-pulse rounded"></div>
      </div>
    </div>
  );
}

export function TopicButton({
  onClick,
  topic,
  searchResult,
}: OmiProps<{ onClick: () => void; topic: Topic } | { onClick: () => void; searchResult: SearchResult }>) {
  const getTopicTitle = (topic: Topic) => {
    if (searchResult) {
      return searchResult.title;
    }

    return isCourse(topic) ? topic.value.metadata?.title || topic.value.id || "" : topic.value;
  };

  const getProjectCount = (topic: Topic) => {
    if (searchResult) {
      return searchResult.entities?.length || 0;
    }

    return isCourse(topic) ? topic.value.metadata?.projects?.length || 0 : 0;
  };

  const backgroundUrl = searchResult ? searchResult.imageUrl : topic.value.metadata?.imageUrl;

  return (
    <button
      className={classNames(
        "animate-fadeInFast card shadow-md border border-brand-200 rounded-2xl p-4 h-52 flex-1 flex flex-col justify-end items-start relative overflow-hidden",
        !backgroundUrl && "bg-leather",
      )}
      onClick={onClick}
    >
      {backgroundUrl && (
        <>
          <div
            className="absolute inset-0 z-0 bg-no-repeat bg-cover bg-center bg-leather"
            style={{
              backgroundImage: `url('${backgroundUrl}')`,
            }}
          />
          <div
            className="absolute top-[40%] bottom-0 left-0 right-0 z-0"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(27, 25, 24, 0.80) 76.71%)",
            }}
          />
        </>
      )}
      <div className="card-header flex flex-col items-start relative z-[1]">
        <h2 className="text-2xl font-semibold mb-1 text-white text-left">{getTopicTitle(topic)}</h2>
        <NWBadge color="gray-filled" padding="xs">
          <span className="normal-case">{getProjectCount(topic)} Projects</span>
        </NWBadge>
      </div>
    </button>
  );
}
